import React from "react";
import { DateRange } from "./DateRangeSelector";
import { useContainerTimeMetrics } from "@/hooks/queries/useContainerTimeMetrics";
import { MetricsConfigurationPanel } from "./MetricsConfigurationPanel";
import { MetricsChartDisplay } from "./MetricsChartDisplay";
import { MetricsStatisticsPanel } from "./MetricsStatisticsPanel";
import { MetricsTableView } from "./MetricsTableView";
import { useContainerTimeChartState } from "@/hooks/useContainerTimeChartState";

interface ContainerTimeChartProps {
  selectedDateRange: DateRange;
}

export const ContainerTimeChart: React.FC<ContainerTimeChartProps> = ({
  selectedDateRange,
}) => {
  const {
    firstContainer,
    setFirstContainer,
    firstEvent,
    setFirstEvent,
    secondContainer,
    setSecondContainer,
    secondEvent,
    setSecondEvent,
    timeUnit,
    setTimeUnit,
    chartType,
    setChartType,
    colorCoding,
    setColorCoding,
    showTable,
    setShowTable,
    currentConfig,
    handleConfigLoad,
    metricsConfig,
    hasValidConfiguration,
  } = useContainerTimeChartState(selectedDateRange);

  const {
    metric,
    isLoading,
    isError,
    error,
    cached,
    isStale,
    backgroundRefresh,
    computedAt,
    refetch,
  } = useContainerTimeMetrics(metricsConfig);

  return (
    <div className="space-y-6">
      <MetricsConfigurationPanel
        firstContainer={firstContainer}
        setFirstContainer={setFirstContainer}
        firstEvent={firstEvent}
        setFirstEvent={setFirstEvent}
        secondContainer={secondContainer}
        setSecondContainer={setSecondContainer}
        secondEvent={secondEvent}
        setSecondEvent={setSecondEvent}
        timeUnit={timeUnit}
        setTimeUnit={setTimeUnit}
        chartType={chartType}
        setChartType={setChartType}
        colorCoding={colorCoding}
        setColorCoding={setColorCoding}
        showTable={showTable}
        setShowTable={setShowTable}
        currentConfig={currentConfig}
        onConfigLoad={handleConfigLoad}
      />

      {hasValidConfiguration ? (
        <>
          <MetricsStatisticsPanel
            metric={metric}
            isLoading={isLoading}
            timeUnit={timeUnit}
          />
          <MetricsChartDisplay
            metric={metric}
            chartType={chartType}
            isLoading={isLoading}
            isError={!!isError}
            error={(error as Error) || null}
            isStale={isStale}
            backgroundRefresh={backgroundRefresh}
            cached={cached}
            computedAt={computedAt}
            onRefresh={() => refetch()}
          />
          {showTable && (
            <MetricsTableView
              metric={metric}
              isLoading={isLoading}
              timeUnit={timeUnit}
            />
          )}
        </>
      ) : (
        <div className="py-12 text-center text-muted-foreground">
          <div className="mb-2 text-lg font-medium">
            Configure Your Analysis
          </div>
          <div className="text-sm">
            Select first and second containers to begin analyzing container time
            metrics.
          </div>
        </div>
      )}
    </div>
  );
};
